import { html2canvas, createJsPdf } from "@/app/report/common/export/lazyLibs"
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "@/hooks/useSearchParams"
import { cloneDeep, isEmpty } from "lodash"
import { useTranslation } from "react-i18next"
import JSZip from "jszip"
import { MatchDayType } from "@/types/activity"
import { readRawDataPlayers } from "@/app/api/report"
import { RawDataFile } from "@/types/report"
import { createGlobalStore } from "@/stores/global"
import { useRecordReportView } from "@/hooks/useRecordReportView"
import type { CreateReportHistoryPayload } from "@/types/reportHistory"

import {
  SectionGraph,
  SummaryImpl,
  SessionDetailsData,
  SectionSessionDetails,
  SectionScatterChart,
  SectionSummary,
  SectionTacticMap,
  SectionPerformanceProfile,
} from "@/app/report/common/constructor"
import { removeSpecialCharacterInAllSource } from "@/app/report/common/tools"
import {
  ReportChartActivity,
  ReportChartFilter,
  ReportChartSession,
  TypeSection,
  ReportCommonProps,
  ReportActivitySessionProps,
  MetricMapType,
  PrintBasicSet,
  ImgData,
  GraphSettings,
  SessionDetailsSettings,
  ScatterChartSettings,
  SummarySettings,
  StatsSettings,
  TacticMapSettings,
  PerformanceProfileSettings,
} from "@/app/report/common/type"
import {
  ACTIVITY_SESSION_TYPE,
  EXPORT_TYPE,
  REPORT_TYPE,
  SECTION_TYPE,
  VALUE_POSITION_TYPE,
} from "@/app/report/common/constants"
import {
  useActivityIntervalSummaryQuery,
  useReportTemplateDetailQuery,
  useReportTemplateMetasQuery,
  useSessionIntervalSummaryQuery,
  useSessionPlayerSummaryQuery,
} from "@/hooks/api/useReportQuery"
import { useActivityDetailQuery } from "@/hooks/api/useActivityQuery"
import { usePlayerSummaryByMode } from "@/app/report/hooks/usePlayerSummaryByMode"
import {
  buildSummarySection,
  selectPlayerSummaryByMode,
} from "@/app/report/common/sections/summary"
import { buildGraphSection } from "@/app/report/common/sections/graph"
import { buildSessionDetailsData } from "@/app/report/common/sections/sessionDetails"
import { buildScatterSection } from "@/app/report/common/sections/scatter"
import { useMetricList } from "@/hooks/api/useMetricQuery"
import { useUnitQuery } from "@/hooks/api/useSettingQuery"
import { usePositionRank } from "@/hooks/usePositionRank"
import { buildReportSortKey, createReportSortStore } from "@/stores/reportSort"
import { createReportColumnFilterStore } from "@/stores/reportColumnFilter"
import { createReportSessionSelectionStore } from "@/stores/reportSessionSelection"
import {
  sortGraphSectionForExport,
  sortSessionDetailsForExport,
} from "@/app/report/common/export/sortRows"
import { inlineStylesheetsIntoClone } from "@/app/report/common/export/inlineStylesheets"
import {
  clearHtml2CanvasIgnore,
  markHtml2CanvasIgnoreOutside,
} from "@/app/report/common/export/html2canvasIgnore"
import { PDF_PREVIEW_WIDTH_PX } from "@/app/report/common/export/constants"
import {
  paginate,
  PAGE_MARGIN_MM,
  CONTENT_WIDTH_MM,
  CHUNK_TYPE,
} from "@/app/report/common/export/paginate"
import {
  collectPdfTargets,
  measureChunkBox,
  chunkImageSizeMm,
  chunkTypeOf,
} from "@/app/report/common/export/chunks"
import {
  applyPreviewPagination,
  clearPreviewPagination,
  waitForStableLayout,
  waitForPreviewContainer,
  PreviewPageBox,
} from "@/app/report/common/export/previewPagination"
import PreviewPageLayer from "@/app/report/components/PreviewPageLayer"
import { MetricMeta } from "@/types/metric"
import {
  Interval,
  IntervalSessionSummaryType,
  PlayerSessionSummary,
  ReportTemplate,
  SummaryType,
} from "@/types/report"
import { Session } from "@/types/session"
import { Unit } from "@/types/setting"
import Icon from "@/components/icon/Icon"
import { colors } from "@/components/colors"
import useRouter from "@/hooks/router/useRoute"

import InformationSummary from "@/app/report/components/InformationSummary"
import PdfProgressOverlay, { PdfProgressHandle } from "@/app/report/components/PdfProgressOverlay"
import ReportFilter from "@/app/report/components/filter/ReportFilter"
import ChartByPlayers from "@/app/report/components/section/ChartByPlayerSection"
import SessionDetails from "@/app/report/components/section/SessionDetailsSection"
import ScatterSection from "@/app/report/components/section/ScatterSection"
import DailyReportSummary from "@/app/report/daily/components/DailyReportSummary"
import CustomTableSection from "@/app/report/components/section/CustomTableSection"
import TacticMapSection from "@/app/report/components/section/TacticMapSection.tsx"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import Dialog from "@/components/dialog/Dialog"
import BasicButton from "@/components/button/BasicButton"

import ExportChartDialog from "@/app/report/components/dialog/ExportChartDialog"
import PrintableChartByPlayerSection from "@/app/report/components/section/pdf/PrintableChartByPlayerSection"
import PrintableSessionDetailSection from "@/app/report/components/section/pdf/PrintableSessionDetailSection"
import PrintableScatterSection from "@/app/report/components/section/pdf/PrintableScatterSection"
import PrintableCustomTableSection from "@/app/report/components/section/pdf/PrintableCustomTableSection"
import PrintableTacticMapSection from "@/app/report/components/section/pdf/PrintableTacticMapSection"

import "./dailyReport.scss"
import PerformanceProfileSection from "../components/section/PerformanceProfileSection"

const DEFAULT_DETAIL_DATA = {
  idfActivity: 0,
  name: "-",
  type: "T" as const,
  dateStart: "-",
  dateFinish: "-",
  analyzeFinished: "-",
  seasonWeek: "-",
  stadium: "-",
  idfStadium: 0,
  matchDay: MatchDayType.NONE,
  players: [],
  sessions: [],
}

const INIT_FILTER = {
  activitySessionType: ACTIVITY_SESSION_TYPE.ACTIVITY,
  selectedActivityIds: [],
  selectedPlayerIds: [],
  selectedSessionIds: [],
  selectedIdfTemplate: [],
}

type DailyReportFilterType = {
  activitySessionType: ACTIVITY_SESSION_TYPE
  selectedActivityIds: number[]
  selectedPlayerIds: number[]
  selectedSessionIds: number[]
  selectedIdfTemplate: number[]
}

export default function DailyReport() {
  const { t } = useTranslation()
  const router = useRouter()

  const searchParams = useSearchParams()
  const urlDailyReportType = useMemo(() => searchParams.get("dailyReportType"), [searchParams])
  const idfActivity = useMemo(
    () => searchParams.get("idfActivity")?.split(",").map(Number),
    [searchParams],
  )
  const urlIdfPlayer = useMemo(
    () => searchParams.get("idfPlayer")?.split(",").map(Number) || [],
    [searchParams],
  )
  const urlIdfSession = useMemo(
    () => searchParams.get("idfSession")?.split(",").map(Number) || [],
    [searchParams],
  )
  const urlIdfTemplate = useMemo(
    () => searchParams.get("idfTemplate")?.split(",").map(Number),
    [searchParams],
  )

  const [filter, setFilter] = useState<DailyReportFilterType>(INIT_FILTER)
  const [reportSections, setReportSections] = useState<TypeSection[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [template, setTemplate] = useState<ReportTemplate | undefined>()
  const [pdfSessionDetails, setPdfSessionDetails] = useState<SectionSessionDetails | undefined>()

  const [activity, setActivity] = useState<ReportChartActivity>({
    intervalSummary: [],
    playerActivitySummary: null,
  })
  const [session, setSession] = useState<ReportChartSession>({
    intervalSummary: [],
    playerSummary: [],
    playerSessionSummary: null,
    selectedSessionIdsForStats: [],
  })
  const [, setSessionsInfo] = useState<Session[]>([])
  const [players, setPlayers] = useState<PlayerSessionSummary>()
  const [interval, setInterval] = useState<Interval>(Interval["5m"])
  const [intervalSelectedSessions, setIntervalSelectedSessions] = useState<number[]>([])
  const [exportPdfOpen, setExportPdfOpen] = useState<boolean>(false)
  const [exportOption, setExportOption] = useState<string>(EXPORT_TYPE.PDF)
  const [previewOpen, setPreviewOpen] = useState<boolean>(false)
  // 미리보기에 그릴 A4 페이지 박스. 출력물과 같은 배치 규칙으로 계산한다.
  const [previewPages, setPreviewPages] = useState<PreviewPageBox[]>([])
  const [currentDecimalPoints, setCurrentDecimalPoints] = useState<number>(1)
  const [currentAnnotationOptions, setCurrentAnnotationOptions] = useState<VALUE_POSITION_TYPE>(
    VALUE_POSITION_TYPE.HIDE_VALUES,
  )
  const [decimalPoints, setDecimalPoints] = useState<number>(1)
  const [annotationOptions, setAnnotationOptions] = useState<VALUE_POSITION_TYPE>(
    VALUE_POSITION_TYPE.HIDE_VALUES,
  )

  const [pdfLoading, setPdfLoading] = useState(false)
  // 진행률은 오버레이 컴포넌트 내부 상태로 격리 (캡처 루프의 진행률 갱신이 리포트/프리뷰를 리렌더하지 않게)
  const pdfProgressRef = useRef<PdfProgressHandle>(null)
  // 생성 중단 요청 플래그. 상태가 아니라 ref 인 이유 — 캡처 루프가 setState 리렌더를
  // 기다리지 않고 즉시 최신값을 읽어야 한다.
  const pdfCancelRef = useRef(false)
  const [printBasicSet] = useState<PrintBasicSet>({
    contentWidth: CONTENT_WIDTH_MM,
    margin: 25,
    startX: 50,
    startY: 50,
    footerY: 60,
    footerFontSize: 8,
  })

  const {
    data: detail,
    isSuccess,
    isError,
  } = useActivityDetailQuery(idfActivity?.[0] ?? 0, !!idfActivity?.length)

  // idfActivity만으로 진입한 경우(액티비티/홈 View Report 등) 액티비티 상세 기준으로
  // 타입·전체 세션·전체 선수를 기본 선택해 자동 렌더 (WEB-2242)
  const dailyReportType = useMemo(() => {
    if (urlDailyReportType || !detail) {
      return urlDailyReportType
    }

    return detail.sessions?.length ? ACTIVITY_SESSION_TYPE.SESSION : ACTIVITY_SESSION_TYPE.ACTIVITY
  }, [urlDailyReportType, detail])
  const idfSession = useMemo(
    () =>
      urlIdfSession.length
        ? urlIdfSession
        : (detail?.sessions?.map((session) => session.idfSession) ?? []),
    [urlIdfSession, detail],
  )
  const idfPlayer = useMemo(
    () =>
      urlIdfPlayer.length
        ? urlIdfPlayer
        : (detail?.players?.map((player) => player.idfPlayer) ?? []),
    [urlIdfPlayer, detail],
  )
  // 템플릿도 URL에 없으면 ReportFilter와 동일 기준(lastUsed → 첫 번째)으로 기본 선택
  const { data: templates } = useReportTemplateMetasQuery({ reportType: REPORT_TYPE.DAILY })
  const idfTemplate = useMemo(() => {
    if (urlIdfTemplate?.length || !templates?.length) {
      return urlIdfTemplate
    }
    const lastUsedTemplate = templates.find((template) => template.isLastUsed)

    return [lastUsedTemplate?.idfTemplate ?? templates[0].idfTemplate]
  }, [urlIdfTemplate, templates])

  const { data: metricList } = useMetricList()
  const { data: selectedUnitList } = useUnitQuery()

  const isActivityMode = filter.activitySessionType === ACTIVITY_SESSION_TYPE.ACTIVITY
  const enabledActivitySummary = !!filter.selectedActivityIds && filter.selectedPlayerIds.length > 0
  const enabledActivity = isSuccess && !isError

  // Report History — 리포트 조회 기록
  const reportHistoryPayload = useMemo<CreateReportHistoryPayload | null>(() => {
    if (!isSuccess || !detail || !idfActivity?.length) return null
    return {
      reportType: REPORT_TYPE.DAILY,
      idfTemplate: idfTemplate?.[0] ?? null,
      activityIds: idfActivity,
      playerIds: idfPlayer,
      sessionIds: idfSession,
      dateStart: searchParams.get("dateStart") ?? "",
      dateFinish: searchParams.get("dateFinish") ?? "",
      activityName: (detail as any)?.name ?? "",
      templateName: null,
    }
  }, [isSuccess, detail, idfActivity, idfPlayer, idfSession, idfTemplate, searchParams])
  useRecordReportView(reportHistoryPayload, isSuccess)

  // 세션 요약은 세션 모드에서만 — 액티비티 모드도 selectedSessionIds 에 전체 세션이 들어오므로
  // (택틱맵·퍼포먼스 프로파일이 쓰는 값) 여기서 걸러야 세션 요약 API 가 불필요하게 호출되지 않는다 (#177)
  const enabledSessionSummary =
    !isActivityMode &&
    !!filter.selectedActivityIds &&
    filter.selectedPlayerIds.length > 0 &&
    filter.selectedSessionIds.length > 0

  const { data: activeTemplate } = useReportTemplateDetailQuery(filter.selectedIdfTemplate[0])

  const positionRank = usePositionRank()
  const sectionSorts = createReportSortStore((state) => state.sorts)
  const syncReportSort = createReportSortStore((state) => state.syncTemplate)
  const syncReportColumnFilter = createReportColumnFilterStore((state) => state.syncTemplate)
  const syncReportSessionSelection = createReportSessionSelectionStore(
    (state) => state.syncTemplate,
  )

  // 템플릿이 실제로 바뀔 때만 이전 화면 정렬·컬럼 필터·섹션 세션 선택(transient)을 폐기 (뒤로가기 복귀 등 재mount 시엔 보존)
  useEffect(() => {
    syncReportSort(REPORT_TYPE.DAILY, filter.selectedIdfTemplate[0])
    syncReportColumnFilter(REPORT_TYPE.DAILY, filter.selectedIdfTemplate[0])
    syncReportSessionSelection(REPORT_TYPE.DAILY, filter.selectedIdfTemplate[0])
  }, [
    filter.selectedIdfTemplate[0],
    syncReportSort,
    syncReportColumnFilter,
    syncReportSessionSelection,
  ])

  // 모드에 맞는 선수 요약 하나만 호출 — PDF 빌더(PdfDataContext)와 같은 훅 (#75)
  const {
    playerActivitySum,
    playerSessionSum,
    isLoading: playerSummaryLoading,
  } = usePlayerSummaryByMode({
    mode: filter.activitySessionType,
    activityId: filter.selectedActivityIds[0],
    sessionIds: filter.selectedSessionIds,
    playerIds: filter.selectedPlayerIds,
    enabled: enabledActivity,
  })

  const { data: sessionPlayerSum, isLoading: sessionPlayerSumLoading } =
    useSessionPlayerSummaryQuery(
      {
        sessions: filter.selectedSessionIds,
        players: filter.selectedPlayerIds,
      },
      enabledSessionSummary && enabledActivity,
    )

  const intervals = [1, 5, 15] as const

  const { data: activityInterval1mData, isLoading: activityInterval1mLoading } =
    useActivityIntervalSummaryQuery(
      {
        interval: "1m" as Interval,
        eventIds: filter.selectedActivityIds,
        players: filter.selectedPlayerIds,
      },
      enabledActivitySummary &&
        enabledActivity &&
        dailyReportType === ACTIVITY_SESSION_TYPE.ACTIVITY,
    )

  const { data: activityInterval5mData, isLoading: activityInterval5mLoading } =
    useActivityIntervalSummaryQuery(
      {
        interval: "5m" as Interval,
        eventIds: filter.selectedActivityIds,
        players: filter.selectedPlayerIds,
      },
      enabledActivitySummary &&
        enabledActivity &&
        dailyReportType === ACTIVITY_SESSION_TYPE.ACTIVITY,
    )

  const { data: activityInterval15mData, isLoading: activityInterval15mLoading } =
    useActivityIntervalSummaryQuery(
      {
        interval: "15m" as Interval,
        eventIds: filter.selectedActivityIds,
        players: filter.selectedPlayerIds,
      },
      enabledActivitySummary &&
        enabledActivity &&
        dailyReportType === ACTIVITY_SESSION_TYPE.ACTIVITY,
    )

  const { data: sessionInterval1mData, isLoading: sessionInterval1mLoading } =
    useSessionIntervalSummaryQuery(
      {
        interval: "1m" as Interval,
        eventIds: intervalSelectedSessions,
        players: filter.selectedPlayerIds,
      },
      enabledSessionSummary &&
        enabledActivity &&
        dailyReportType === ACTIVITY_SESSION_TYPE.SESSION &&
        !!intervalSelectedSessions.length,
    )

  const { data: sessionInterval5mData, isLoading: sessionInterval5mLoading } =
    useSessionIntervalSummaryQuery(
      {
        interval: "5m" as Interval,
        eventIds: intervalSelectedSessions,
        players: filter.selectedPlayerIds,
      },
      enabledSessionSummary &&
        enabledActivity &&
        dailyReportType === ACTIVITY_SESSION_TYPE.SESSION &&
        !!intervalSelectedSessions.length,
    )

  const { data: sessionInterval15mData, isLoading: sessionInterval15mLoading } =
    useSessionIntervalSummaryQuery(
      {
        interval: "15m" as Interval,
        eventIds: intervalSelectedSessions,
        players: filter.selectedPlayerIds,
      },
      enabledSessionSummary &&
        enabledActivity &&
        dailyReportType === ACTIVITY_SESSION_TYPE.SESSION &&
        !!intervalSelectedSessions.length,
    )

  const activityIntervalSummaries = useMemo(
    () => [
      { data: activityInterval1mData, isLoading: activityInterval1mLoading },
      { data: activityInterval5mData, isLoading: activityInterval5mLoading },
      { data: activityInterval15mData, isLoading: activityInterval15mLoading },
    ],
    [
      activityInterval1mData,
      activityInterval1mLoading,
      activityInterval5mData,
      activityInterval5mLoading,
      activityInterval15mData,
      activityInterval15mLoading,
    ],
  )

  const sessionIntervalSummaries = useMemo(
    () => [
      { data: sessionInterval1mData, isLoading: sessionInterval1mLoading },
      { data: sessionInterval5mData, isLoading: sessionInterval5mLoading },
      { data: sessionInterval15mData, isLoading: sessionInterval15mLoading },
    ],
    [
      sessionInterval1mData,
      sessionInterval1mLoading,
      sessionInterval5mData,
      sessionInterval5mLoading,
      sessionInterval15mData,
      sessionInterval15mLoading,
    ],
  )

  useEffect(() => {
    setFilter(INIT_FILTER)
  }, [])

  useEffect(() => {
    const newActivity = cloneDeep(activity)
    const newSession = cloneDeep(session)

    // 같은 화면에서 세션↔액티비티 모드를 바꾸면 이전 모드의 요약이 남아 섹션이 옛 값을 쓰므로
    // 현재 모드의 반대편은 비운다 (#177)
    if (filter.activitySessionType === ACTIVITY_SESSION_TYPE.ACTIVITY) {
      newSession.playerSummary = []
      newSession.playerSessionSummary = null
      newSession.intervalSummary = []
    } else {
      newActivity.playerActivitySummary = null
      newActivity.intervalSummary = []
    }

    if (playerActivitySum) {
      newActivity.playerActivitySummary = playerActivitySum
    }
    if (playerSessionSum && sessionPlayerSum) {
      newSession.playerSummary = sessionPlayerSum
      newSession.playerSessionSummary = playerSessionSum
    }

    activityIntervalSummaries.forEach(({ data }, index) => {
      if (data) {
        updateIntervalSummary(intervals[index], data)
      }
    })
    sessionIntervalSummaries.forEach(({ data }, index) => {
      if (data) {
        updateIntervalSummary(intervals[index], data)
      }
    })
    setActivity(newActivity)
    setSession(newSession)

    function updateIntervalSummary(intervalMin: number, intervalSum: IntervalSessionSummaryType[]) {
      switch (filter.activitySessionType) {
        case ACTIVITY_SESSION_TYPE.ACTIVITY:
          {
            if (
              !isEmpty(filter.selectedActivityIds) &&
              isEmpty(newActivity.intervalSummary[intervalMin]) &&
              intervalSum.length
            ) {
              newActivity.intervalSummary[intervalMin] = intervalSum

              break
            }
          }
          break

        case ACTIVITY_SESSION_TYPE.SESSION:
          {
            if (!isEmpty(filter.selectedSessionIds)) {
              const intervalSummaries = intervalSum
              if (intervalSummaries.length) {
                newSession.intervalSummary[intervalMin] = intervalSummaries

                break
              }
            }
          }
          break
      }
    }
  }, [
    filter.activitySessionType,
    playerActivitySum,
    playerSessionSum,
    sessionPlayerSum,
    ...activityIntervalSummaries.map(({ data }) => data),
    ...sessionIntervalSummaries.map(({ data }) => data),
  ])

  useEffect(() => {
    if (
      (dailyReportType === ACTIVITY_SESSION_TYPE.ACTIVITY &&
        (!idfActivity?.length || !idfPlayer.length)) ||
      (dailyReportType === ACTIVITY_SESSION_TYPE.SESSION &&
        (!idfActivity?.length || !idfPlayer.length || !idfSession.length))
    ) {
      setFilter(INIT_FILTER)
    } else {
      setFilter({
        activitySessionType: dailyReportType || ACTIVITY_SESSION_TYPE.ACTIVITY,
        selectedActivityIds: idfActivity || [],
        selectedPlayerIds: idfPlayer || [],
        selectedSessionIds: idfSession || [],
        selectedIdfTemplate: idfTemplate || [],
      })
    }
  }, [dailyReportType, idfActivity, idfPlayer, idfSession, idfTemplate])

  useEffect(() => {
    if (activeTemplate) {
      setTemplate(activeTemplate)
    }
  }, [activeTemplate])

  useEffect(() => {
    createReport({
      filter,
      template: template as ReportTemplate,
      metricList: metricList || [],
      selectedUnitList: selectedUnitList || [],
      players: players || { players: [] },
      t,
      setLoading,
      setReportSections,
      sessionsInfo: detail?.sessions || [],
      activity,
      session,
      interval,
      translateKey,
      setPdfSessionDetails,
    })
  }, [
    template,
    metricList,
    selectedUnitList,
    activity,
    session,
    players,
    t,
    filter,
    interval,
    detail,
  ])

  useEffect(() => {
    const data = cloneDeep(
      dailyReportType === ACTIVITY_SESSION_TYPE.ACTIVITY ? playerActivitySum : playerSessionSum,
    )

    if (data) {
      const totalSummary = new SummaryImpl()
      const avgSummary = new SummaryImpl()

      if (data.total) {
        totalSummary.setReport(data.total as SummaryType)
        data.total = totalSummary
      }

      if (data.average) {
        avgSummary.setReport(data.average as SummaryType)
        data.average = avgSummary
      }

      data.players.forEach((player) => {
        const playerSummary = new SummaryImpl()
        playerSummary.setReport(player.summary as SummaryType)

        player.summary = playerSummary
      })
    }
    setPlayers(data)
  }, [playerActivitySum, playerSessionSum, searchParams])

  useEffect(() => {
    setIntervalSelectedSessions(idfSession.slice(0, 1))
  }, [searchParams])

  const translateKey = useTranslateMetricKey()

  const renderSection = () => {
    return reportSections.map((section, index) => {
      const data = section
      if (section.type === SECTION_TYPE.SUMMARY) {
        return (
          <DailyReportSummary
            key={`${section.type}-${index}`}
            data={data as SectionSummary}
            loading={loading}
          />
        )
      }
      const defaultLoading = loading || playerSummaryLoading || sessionPlayerSumLoading

      if (section.type === SECTION_TYPE.GRAPH) {
        return (
          <ChartByPlayers
            key={`${section.type}-${index}`}
            reportSection={data as SectionGraph}
            loading={defaultLoading}
            sectionIndex={index}
            reportType={REPORT_TYPE.DAILY}
          />
        )
      }
      if (section.type === SECTION_TYPE.SESSION_DETAILS) {
        return (
          <SessionDetails
            key={`${section.type}-${index}`}
            reportSection={data as SectionSessionDetails}
            loading={defaultLoading}
            intervalLoading={
              activityIntervalSummaries.some(({ isLoading }) => isLoading) ||
              sessionIntervalSummaries.some(({ isLoading }) => isLoading)
            }
            intervalValue={interval}
            setIntervalValue={setInterval}
            selectedSession={intervalSelectedSessions}
            setSelectedSession={setIntervalSelectedSessions}
            sectionIndex={index}
            reportType={REPORT_TYPE.DAILY}
          />
        )
      }
      if (section.type === SECTION_TYPE.SCATTER_GRAPH) {
        return (
          <ScatterSection
            key={`${section.type}-${index}`}
            reportSection={data as SectionScatterChart}
            loading={defaultLoading}
          />
        )
      }
      if (section.type === SECTION_TYPE.TACTIC_MAP) {
        return (
          <TacticMapSection
            key={`${section.type}-${index}`}
            reportSection={data as SectionTacticMap}
            activity={detail ?? DEFAULT_DETAIL_DATA}
            idfSessions={filter.selectedSessionIds}
            idfPlayers={filter.selectedPlayerIds}
            loading={defaultLoading}
          />
        )
      }
      if (section.type === SECTION_TYPE.PERFORMANCE_PROFILE) {
        return (
          <PerformanceProfileSection
            key={`${section.type}-${index}`}
            reportSection={data as SectionPerformanceProfile}
            activity={detail ?? DEFAULT_DETAIL_DATA}
            idfSessions={filter.selectedSessionIds}
            idfPlayers={filter.selectedPlayerIds}
            loading={defaultLoading}
          />
        )
      }
      if (section.type === SECTION_TYPE.CUSTOM_TABLE) {
        const customTableSection = section as unknown as { type: string; settings: StatsSettings }

        // customTable 은 클라이언트 정렬만 — sortSetting 저장 미지원이라 템플릿 PUT(onSortChange) 미연결 (WEB-2193)
        return (
          <CustomTableSection
            key={`${section.type}-${index}`}
            settings={customTableSection.settings}
            reportType={REPORT_TYPE.DAILY}
            sectionIndex={index}
            sessionMode={filter.activitySessionType === ACTIVITY_SESSION_TYPE.SESSION}
            sessionList={detail?.sessions || []}
            reportSessionIds={filter.selectedSessionIds}
          />
        )
      }

      return null
    })
  }

  // PDF 프리뷰용 정렬 데이터는 메모이즈해서 참조를 안정화한다.
  // 렌더마다 새 객체를 만들면 setPdfLoading 등 리렌더 시 차트가 다시 그려지는데,
  // 그 시점의 scaleRatio(첫 렌더 1 → 이후 컨테이너 실측 ~2)가 달라 확대-오버플로우로 차트가 빈 것처럼 보인다.
  const pdfSortedSections = useMemo(() => {
    return reportSections.map((section, index) => {
      if (!section) {
        return section
      }
      if (section.type === SECTION_TYPE.GRAPH) {
        return sortGraphSectionForExport(
          section as SectionGraph,
          positionRank,
          (chartIndex) => sectionSorts[buildReportSortKey(REPORT_TYPE.DAILY, index, chartIndex)],
        )
      }

      return section
    })
  }, [reportSections, sectionSorts, positionRank, t])

  const pdfSortedSessionDetails = useMemo(() => {
    if (!pdfSessionDetails) {
      return undefined
    }
    const sectionIndex = reportSections.findIndex((s) => s?.type === SECTION_TYPE.SESSION_DETAILS)

    return sortSessionDetailsForExport(
      pdfSessionDetails,
      positionRank,
      sectionSorts[buildReportSortKey(REPORT_TYPE.DAILY, sectionIndex, 0)],
    )
  }, [pdfSessionDetails, reportSections, sectionSorts, positionRank])

  // 미리보기 페이지 배치. 차트가 다 그려진 뒤에 재야 높이가 맞으므로 레이아웃이 안정될 때까지
  // 기다렸다가 계산한다. 덩어리 위치는 margin 으로만 조정하므로 캡처 결과에는 영향이 없다.
  useEffect(() => {
    const container = document.querySelector(".ExportPdfPreviewCont") as HTMLElement | null

    if (!previewOpen) {
      clearPreviewPagination(container)
      setPreviewPages([])

      return
    }

    let cancelled = false

    const run = async () => {
      const target = await waitForPreviewContainer(".ExportPdfPreviewCont")
      if (!target || cancelled) {
        return
      }

      await document.fonts.ready
      await waitForStableLayout(target)

      if (cancelled) {
        return
      }

      setPreviewPages(applyPreviewPagination(target, printBasicSet.contentWidth))
    }

    run()

    return () => {
      cancelled = true
    }
  }, [
    previewOpen,
    reportSections,
    pdfSortedSections,
    pdfSortedSessionDetails,
    annotationOptions,
    decimalPoints,
    printBasicSet.contentWidth,
  ])

  const renderPdfSection = useCallback(() => {
    // DOM 을 재지 않는다 — 다이얼로그가 마운트되기 전 렌더에서는 1 이 잡혔다가 캡처 직전
    // 리렌더에서 실측값으로 바뀌어, 사용자가 본 미리보기와 실제 캡처가 달랐다.
    const scaleRatio = PDF_PREVIEW_WIDTH_PX / 1000

    return reportSections.map((section, index) => {
      const data = section

      if (section.type === SECTION_TYPE.SUMMARY) {
        return (
          <DailyReportSummary
            key={`pdf-${section.type}-${index}`}
            data={data as SectionSummary}
            loading={false}
          />
        )
      }
      if (section.type === SECTION_TYPE.GRAPH) {
        return (
          <PrintableChartByPlayerSection
            key={`pdf-${section.type}-${index}`}
            isFirst={index === 0}
            data={pdfSortedSections[index] as SectionGraph}
            currentDecimalPoint={decimalPoints}
            currentChartAnnotation={annotationOptions}
            scaleRatio={scaleRatio || 1}
          />
        )
      }
      if (section.type === SECTION_TYPE.SESSION_DETAILS) {
        return (
          <PrintableSessionDetailSection
            key={`pdf-${section.type}-${index}`}
            data={pdfSortedSessionDetails as SectionSessionDetails}
            scaleRatio={scaleRatio || 1}
            currentDecimalPoint={decimalPoints}
            currentChartAnnotation={annotationOptions}
          />
        )
      }
      if (section.type === SECTION_TYPE.SCATTER_GRAPH) {
        return (
          <PrintableScatterSection
            key={`pdf-${section.type}-${index}`}
            data={data as SectionScatterChart}
            scaleRatio={scaleRatio || 1}
          />
        )
      }
      if (section.type === SECTION_TYPE.TACTIC_MAP) {
        return (
          <PrintableTacticMapSection
            key={`pdf-${section.type}-${index}`}
            reportSection={data as SectionTacticMap}
            activity={detail ?? DEFAULT_DETAIL_DATA}
            idfSessions={filter.selectedSessionIds}
            idfPlayers={filter.selectedPlayerIds}
          />
        )
      }
      // 퍼포먼스 프로파일은 PDF 에 넣지 않는다 (화면에서만 본다)
      if (section.type === SECTION_TYPE.PERFORMANCE_PROFILE) {
        return null
      }
      if (section.type === SECTION_TYPE.CUSTOM_TABLE) {
        const customTableSection = section as unknown as { type: string; settings: StatsSettings }

        return (
          <PrintableCustomTableSection
            key={`pdf-${section.type}-${index}`}
            settings={customTableSection.settings}
            sectionIndex={index}
            scaleRatio={scaleRatio || 1}
            currentDecimalPoint={decimalPoints}
            reportType={REPORT_TYPE.DAILY}
            isFirst={index === 0}
            sessionMode={filter.activitySessionType === ACTIVITY_SESSION_TYPE.SESSION}
            reportSessionIds={filter.selectedSessionIds}
          />
        )
      }
    })
  }, [
    annotationOptions,
    decimalPoints,
    reportSections,
    pdfSortedSections,
    pdfSortedSessionDetails,
    detail,
    filter.selectedSessionIds,
    filter.selectedPlayerIds,
    filter.activitySessionType,
  ])

  const parseImage = async (html: HTMLElement): Promise<ImgData[]> => {
    const backGroundImg = new window.Image()
    const mapBoundingRect: Array<{ rect: DOMRect; type: string | undefined }> = []

    if (html.classList.contains("map")) {
      // 필요시 지도 svg 처리
    }

    let scale = 2

    if (html.classList.contains("TacticMapSection")) {
      scale = 1.5
    } else if (html.classList.contains("PerformanceProfileSection")) {
      scale = 1.5
    } else if (html.classList.contains("DailyReportSummaryContainer")) {
      scale = 1.5
    } else if (html.classList.contains("ChartByPlayerSection")) {
      scale = 1.5
    }

    const pdfContainer = document.querySelector(".ExportPdfPreviewCont")
    const containerWidth = pdfContainer?.clientWidth || html.scrollWidth

    // 표는 캡처 직전 화면에 띄워야 스크롤 폭이 제대로 잡힌다.
    if (html.classList.contains("extracted-individual-table") || html.tagName === "TABLE") {
      html.scrollIntoView({ block: "start", behavior: "instant" })
      await new Promise((resolve) => setTimeout(resolve, 50))
    }

    // 캡처 박스와 mm 환산은 common/export/chunks 가 정한다 — 미리보기 페이징이 같은 값을 쓴다.
    // 페이지 비율(267/190) 패딩 없이 내용 크기 그대로 캡처한다. 페이지 높이로 패딩하면 흰 여백이
    // 이미지에 포함돼 남은공간 배치·분할 시 상단 대공백/백지 페이지/행 잘림으로 나타난다 (WEB-2264)
    const chunkBox = measureChunkBox(html, containerWidth)
    const htmlWidth = chunkBox.htmlWidth
    const htmlHeight = chunkBox.htmlHeight

    await document.fonts.ready

    return html2canvas(html, {
      useCORS: true,
      allowTaint: true,
      backgroundColor: colors.neutral.white,
      scale: scale,
      height: htmlHeight,
      width: htmlWidth,
      windowHeight: htmlHeight,
      // 복제본은 미리보기와 같은 폭으로 레이아웃해야 한다. 캡처 대상의 폭(예: 분할된 표 250px)을
      // 주면 복제본 전체가 그 폭으로 눌려 표 컬럼이 min-content 로 찌그러진다.
      windowWidth: containerWidth,
      onclone: (clonedDoc) => {
        inlineStylesheetsIntoClone(clonedDoc)

        const dialogBg = clonedDoc.querySelector(".DialogBg") as HTMLElement
        if (dialogBg) {
          dialogBg.style.display = "none"
        }

        const chartContainers = clonedDoc.querySelectorAll(".element-chart-container")
        chartContainers.forEach((container) => {
          ;(container as HTMLElement).style.overflow = "visible"
        })

        const tableContainers = clonedDoc.querySelectorAll(".table-container")
        tableContainers.forEach((container) => {
          ;(container as HTMLElement).style.overflow = "visible"
        })

        // 미리보기와 같은 폭으로 고정한다 — 캡처 대상의 폭으로 잡으면 안 된다.
        // 분할된 표(예: 마지막 조각 250px)를 캡처할 때 컨테이너까지 250px 로 눌리면서
        // 컬럼이 min-content 로 찌그러지고(45/104/101 -> 19/104/19) 헤더가 세로로 풀려,
        // 표가 142x727 이 되어 캡처 범위 밖으로 행이 잘려 나갔다.
        const renewalContainers = clonedDoc.querySelectorAll(".renewal-pdf-container")
        renewalContainers.forEach((container) => {
          ;(container as HTMLElement).style.width = `${containerWidth}px`
          ;(container as HTMLElement).style.minWidth = `${containerWidth}px`
        })

        const sections = clonedDoc.querySelectorAll(".section")
        sections.forEach((section) => {
          ;(section as HTMLElement).style.width = `${containerWidth}px`
          ;(section as HTMLElement).style.minWidth = `${containerWidth}px`
        })

        const summaryContainers = clonedDoc.querySelectorAll(".DailyReportSummaryCont")
        summaryContainers.forEach((container) => {
          ;(container as HTMLElement).style.width = `${htmlWidth}px`
          ;(container as HTMLElement).style.minWidth = `${htmlWidth}px`
        })

        const svgItems = clonedDoc.querySelectorAll("svg")
        const regexQuote = new RegExp("'", "g")

        const regexBackSpace = new RegExp("\b", "g")
        let container: HTMLElement | SVGElement | null = null
        svgItems.forEach((svg) => {
          const existingStyle = svg.querySelector("style")
          const fontStyle = `
            @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.min.css');
            text, tspan { font-family: 'Pretendard', 'pretendard', sans-serif !important; }
          `
          if (existingStyle) {
            existingStyle.textContent = fontStyle + existingStyle.textContent
          } else {
            const styleElement = document.createElementNS("http://www.w3.org/2000/svg", "style")
            styleElement.textContent = fontStyle
            svg.insertBefore(styleElement, svg.firstChild)
          }

          container = svg
          while (container && container.parentElement) {
            container = container.parentElement
            if (container.classList.value.includes("renewal-pdf map")) {
              if (html.classList.contains("empty")) {
                svg.style.filter = "blur(10px)"
              }
              const isLocation = container.classList.value.includes("location")
              const isFormation = container.classList.value.includes("formation")
              let type: string | undefined
              if (isLocation) {
                type = "location"
              } else if (isFormation) {
                type = "formation"
              } else {
                type = undefined
              }
              mapBoundingRect.push({ rect: container.getBoundingClientRect(), type })
              break
            }
          }
          svg.innerHTML = svg.innerHTML.replace(regexBackSpace, "").replace(regexQuote, "&apos;")
        })
      },
    }).then((canvas) => {
      const image: ImgData[] = []
      const { imgWidth, imgHeight } = chunkImageSizeMm(
        chunkBox,
        containerWidth,
        printBasicSet.contentWidth,
      )
      html.setAttribute("data-html2canvas-ignore", "")

      const isLocation = html.classList.value.includes("renewal-pdf map location")
      const isFormation = html.classList.value.includes("renewal-pdf map formation")

      if (html.classList.contains("map") && backGroundImg.src) {
        const ctx = canvas.getContext("2d")
        if (ctx && html.classList.contains("empty")) {
          ctx.filter = "blur(10px)"
        }
        let rect
        if (isLocation) {
          rect = mapBoundingRect.find((item) => item.type === "location")?.rect
        } else if (isFormation) {
          rect = mapBoundingRect.find((item) => item.type === "formation")?.rect
        } else {
          rect = mapBoundingRect.find((item) => item.type === undefined)?.rect
        }
        const mapElement = html.children[2]
        const sectionTitleElement = html.children[0]
        const firstPaddingTop = parseFloat(
          window.getComputedStyle(mapElement).getPropertyValue("padding-top"),
        )
        ctx?.drawImage(
          backGroundImg,
          (rect?.width as number) / 2 + (rect?.left as number) - backGroundImg.width / 2,
          (rect?.top as number) +
            mapElement.getBoundingClientRect().top -
            sectionTitleElement.getBoundingClientRect().top +
            firstPaddingTop,
          backGroundImg.width,
          backGroundImg.height,
        )
        if (isLocation && mapElement.children[1]) {
          const secondPaddingTop = parseFloat(
            window.getComputedStyle(mapElement.children[1]).getPropertyValue("padding-top"),
          )
          ctx?.drawImage(
            backGroundImg,
            (rect?.width as number) / 2 + (rect?.left as number) - backGroundImg.width / 2,
            (rect?.top as number) +
              mapElement.children[1].getBoundingClientRect().top -
              sectionTitleElement.getBoundingClientRect().top +
              secondPaddingTop,
            backGroundImg.width,
            backGroundImg.height,
          )
        }
      }

      const imgData = canvas.toDataURL("image/jpeg", 0.8)
      image.push({
        data: imgData,
        width: imgWidth,
        height: imgHeight,
        class: html.classList,
        chunkType: chunkTypeOf(html),
      })

      return image
    })
  }

  // 페이지 배치 — 섹션을 원자 단위로 다룬다.
  //
  // 이전에는 이미지를 mm 공간에서 잘라 붙이며 표 / 전술맵 / 일반 섹션마다 거의 같은 분할
  // 코드를 세 벌 반복했다. 그 결과 위쪽(섹션 생성)과 아래쪽(mm 산술)이 서로 다른 기준으로
  // 두 번 나누게 되어 백지 페이지·상단 대공백·행 잘림이 반복됐다 (WEB-2264).
  //
  // Printable* 컴포넌트들은 이미 "한 페이지에 들어갈 덩어리" 로 잘라서 내놓는다
  // (MAX_CHART_PER_PAGE / TABLE_PER_PAGE_BY_ROW 주석이 그 의도를 명시한다).
  // 그래서 여기서는 그 결과를 믿고 "들어가면 놓고, 안 들어가면 페이지를 넘긴다" 만 한다.
  // 한 페이지보다 큰 덩어리만 예외적으로 잘라 넣는다.
  // 페이지 배치는 common/export/paginate 가 전담한다 — 미리보기와 출력이 갈라지지 않게
  // "어디에 놓을지"를 한 곳에서만 계산한다. 여기서는 계산된 자리에 그리기만 한다.
  const makeReportPdf = async (imageDataArray: ImgData[]) => {
    const doc = await createJsPdf({
      orientation: "p",
      unit: "mm",
      format: "a4",
    })

    // Pretendard(커스텀) 폰트는 추가하지 않는다.
    // jsPDF 는 출력(buildDocument) 시 추가된 모든 커스텀 폰트를 임베드/서브셋하는데,
    // CDN 로드 실패/OTF 등으로 글리프 데이터가 없으면 glyphFor 에서 크래시해 PDF 생성이 통째로 실패한다.
    // 리포트 본문은 전부 이미지(html2canvas), 텍스트는 footer 페이지번호(숫자)뿐이라 내장 helvetica 로 충분.

    const pageHeight = doc.internal.pageSize.height
    const pageWidth = doc.internal.pageSize.width
    // 컨텐츠 좌변 고정 — 폭이 좁은 테이블도 다른 섹션과 좌측 정렬 (WEB-2276)
    const xOffset = (pageWidth - printBasicSet.contentWidth) / 2

    const { placements } = paginate(
      imageDataArray.map((imageData) => imageData.height),
      {
        types: imageDataArray.map((imageData) => imageData.chunkType ?? CHUNK_TYPE.CHART),
      },
    )

    let currentPage = 1

    const drawFooter = () => {
      const footerText = `${currentPage}`
      const xFooterOffset =
        pageWidth / 2 - (doc.getStringUnitWidth(footerText) * printBasicSet.footerFontSize) / 2
      doc.setFontSize(printBasicSet.footerFontSize)
      doc.setFont("helvetica", "normal")
      doc.setTextColor(colors.neutral.grey["800"])
      doc.text(footerText, xFooterOffset, pageHeight - PAGE_MARGIN_MM)
    }

    // 배치 결과의 페이지까지 따라간다. addPage 는 여기서만 부르고, 부른 직후 반드시
    // 무언가를 그리는 경로에서만 호출되므로 백지 페이지가 생기지 않는다.
    const goToPage = (page: number) => {
      while (currentPage < page) {
        drawFooter()
        doc.addPage()
        currentPage++
      }
    }

    const loadImage = (src: string) =>
      new Promise<HTMLImageElement>((resolve) => {
        const img = new window.Image()
        img.src = src
        if (img.complete) {
          resolve(img)

          return
        }
        img.onload = () => resolve(img)
      })

    for (const placement of placements) {
      if (!placement.placed) {
        continue
      }

      const imageData = imageDataArray[placement.index]

      if (placement.slices.length === 0) {
        goToPage(placement.page)
        doc.addImage(
          imageData.data,
          "jpeg",
          xOffset,
          placement.y,
          imageData.width,
          placement.height,
        )
        continue
      }

      // 한 페이지보다 큰 덩어리 — 조각마다 원본에서 해당 구간만 잘라 그린다.
      const img = await loadImage(imageData.data)
      const pdfToImgRatio = img.height / placement.height

      for (const slice of placement.slices) {
        goToPage(slice.page)

        const canvas = document.createElement("canvas")
        canvas.width = img.width
        canvas.height = slice.height * pdfToImgRatio

        const ctx = canvas.getContext("2d")
        if (ctx) {
          ctx.fillStyle = colors.neutral.white
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          ctx.drawImage(
            img,
            0,
            slice.sourceOffset * pdfToImgRatio,
            img.width,
            canvas.height,
            0,
            0,
            img.width,
            canvas.height,
          )
        }

        doc.addImage(
          canvas.toDataURL("image/jpeg", 0.8),
          "jpeg",
          xOffset,
          slice.y,
          imageData.width,
          slice.height,
        )
      }
    }

    drawFooter()

    // window.open(blob) 은 긴 캡처(async) 뒤 호출이라 브라우저 팝업 차단에 걸려 큰 리포트에서 PDF 가
    // 안 열렸다. 팝업 차단 안 걸리는 다운로드로 전달을 보장한다.
    const fileName = `${(detail?.name || "report").replace(/[\\/:*?"<>|]/g, "_")}.pdf`
    doc.save(fileName)
  }

  const generatePdf = async () => {
    pdfCancelRef.current = false
    setPdfLoading(true)
    pdfProgressRef.current?.start()

    await new Promise((resolve) => setTimeout(resolve, 100))

    await document.fonts.ready

    const container = document.querySelector(".ExportPdfPreviewCont")

    // 이전 실행에서 남긴 제외 표시 제거 (재시도 시 빈 캡처 방지). 캡처 대상 밖에도 붙으므로 문서 전역으로 지운다.
    clearHtml2CanvasIgnore()

    // 캡처 대상 경로 밖(앱 셸·라이브 리포트·다이얼로그 배경)을 제외해 복제 범위를 좁힌다.
    // html2canvas 는 호출마다 문서를 통째로 복제하고, 그 비용이 생성 시간의 대부분을 차지한다.
    markHtml2CanvasIgnoreOutside(container)

    try {
      const htmlElements = collectPdfTargets(container)

      const imageDataList: ImgData[] = []
      const totalSections = htmlElements.length

      for (let i = 0; i < htmlElements.length; i++) {
        // 취소는 섹션 경계에서만 확인한다 — html2canvas 가 메인 스레드를 점유해
        // 한 섹션을 캡처하는 동안에는 이벤트 루프가 돌지 않는다.
        if (pdfCancelRef.current) {
          return
        }

        const currentSection = i + 1
        const progress = Math.floor(10 + (currentSection / totalSections) * 80)

        pdfProgressRef.current?.setProgress(progress)

        await new Promise((resolve) => setTimeout(resolve, 50))

        const sectionResult = await parseImage(htmlElements[i])
        imageDataList.push(...sectionResult)
      }

      if (pdfCancelRef.current) {
        return
      }

      pdfProgressRef.current?.setProgress(95)

      await new Promise((resolve) => setTimeout(resolve, 200))

      await makeReportPdf(imageDataList as ImgData[])

      pdfProgressRef.current?.setProgress(100)
    } catch (error) {
      alert(error)
    } finally {
      clearHtml2CanvasIgnore()

      // 취소 시에는 완료 연출(800ms) 없이 즉시 닫는다.
      const closeDelay = pdfCancelRef.current ? 0 : 800
      setTimeout(() => {
        setPdfLoading(false)
        pdfProgressRef.current?.finish()
      }, closeDelay)
    }
  }

  const downloadRawData = async () => {
    if (!idfActivity?.[0] || !filter.selectedPlayerIds.length) {
      return
    }

    const setSnackBar = createGlobalStore.getState().setSnackBar

    try {
      const playersResponse = await readRawDataPlayers({
        idfActivity: idfActivity[0],
        idfPlayers: filter.selectedPlayerIds,
      })

      const allFiles: RawDataFile[] = playersResponse.response?.files || []

      if (allFiles.length === 0) {
        setSnackBar({
          open: true,
          message: t("messages.noDataAvailable") || "No data to download",
          severity: "warning",
        })

        return
      }

      const zip = new JSZip()

      await Promise.all(
        allFiles.map(async (file) => {
          try {
            const response = await fetch(file.url)
            const blob = await response.blob()
            zip.file(file.fileName, blob)
          } catch (error) {
            console.error(`Failed to download file: ${file.fileName}`, error)
          }
        }),
      )

      const zipBlob = await zip.generateAsync({ type: "blob" })
      const zipName = playersResponse.response?.zipName || "raw_data.zip"

      const downloadUrl = URL.createObjectURL(zipBlob)
      const link = document.createElement("a")
      link.href = downloadUrl
      link.download = zipName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(downloadUrl)

      setSnackBar({
        open: true,
        message: t("messages.downloadComplete", "Download complete"),
        severity: "success",
      })
    } catch (error) {
      console.error("Failed to download raw data:", error)
      setSnackBar({
        open: true,
        message: t("messages.downloadFailed", "Failed to download raw data"),
        severity: "error",
      })
    }
  }

  return (
    <div className="DailyReportCont">
      <div
        className="daily-report-setting-icon"
        onClick={() => router?.push("/settings/report/daily")}>
        <Icon type="settings" size={28} color={colors.neutral.grey[600]} />
      </div>
      <ReportFilter reportType={REPORT_TYPE.DAILY} setSessionsInfo={setSessionsInfo} />

      <InformationSummary
        type={REPORT_TYPE.DAILY}
        data={detail ?? DEFAULT_DETAIL_DATA}
        playersData={detail?.players?.filter((player) => idfPlayer?.includes(player.idfPlayer))}
        onClickExportPDF={() => {
          setExportPdfOpen(true)
        }}
      />

      {renderSection()}

      <ExportChartDialog
        open={exportPdfOpen}
        setOpen={setExportPdfOpen}
        exportOptions={[EXPORT_TYPE.PDF, EXPORT_TYPE.RAW_DATA]}
        exportOption={exportOption}
        onChangeExportOption={(value) => {
          setExportOption(value)
        }}
        decimalPoints={currentDecimalPoints}
        annotationOptions={currentAnnotationOptions}
        onChangeDecimalPoints={setCurrentDecimalPoints}
        onChangeAnnotationOptions={setCurrentAnnotationOptions}
        onConfirm={() => {
          if (exportOption === EXPORT_TYPE.PDF) {
            setDecimalPoints(currentDecimalPoints)
            setAnnotationOptions(currentAnnotationOptions)
            setPreviewOpen(true)
          } else if (exportOption === EXPORT_TYPE.RAW_DATA) {
            downloadRawData()
          }
        }}
      />
      {/* TODO preview dialog */}
      <Dialog
        open={previewOpen}
        title={t("preview")}
        footer={
          <>
            <BasicButton
              type="default-outlined"
              onClick={() => setPreviewOpen(false)}
              size="md"
              disabled={pdfLoading}>
              {t("button.close")}
            </BasicButton>
            <BasicButton
              onClick={generatePdf}
              size="md"
              type="primary-contained"
              disabled={pdfLoading}>
              {t("pdfPrint")}
            </BasicButton>
          </>
        }
        onClose={() => {
          // 생성 중에는 배경 클릭·ESC·닫기 버튼으로 닫히지 않는다.
          // 캡처 대상 DOM 이 이 다이얼로그 안에 있어, 닫히면 생성이 그대로 깨진다.
          if (pdfLoading) {
            return
          }
          setPreviewOpen(false)
        }}>
        <div
          className="ExportPdfPreviewCont"
          style={{
            width: PDF_PREVIEW_WIDTH_PX,
            minWidth: PDF_PREVIEW_WIDTH_PX,
            margin: "0 auto",
            // 마지막 페이지 박스는 absolute 라 컨테이너 높이에 안 잡힌다 — 직접 확보한다.
            minHeight: previewPages.length
              ? `${previewPages[previewPages.length - 1].topPx + previewPages[previewPages.length - 1].heightPx}px`
              : undefined,
          }}>
          <PreviewPageLayer pages={previewPages} />
          <style>{`
            .ExportPdfPreviewCont .ReportSectionCont {
              box-shadow: none !important;
            }
            .ExportPdfPreviewCont .section-name {
              background-color: white !important;
            }
            .ExportPdfPreviewCont .ReportSectionTitleCont {
              background-color: white !important;
            }
          `}</style>
          <InformationSummary
            type={REPORT_TYPE.DAILY}
            data={detail ?? DEFAULT_DETAIL_DATA}
            playersData={detail?.players?.filter((player) => idfPlayer?.includes(player.idfPlayer))}
            isDialog
          />
          {renderPdfSection()}

          {/* PDF 생성 로딩 오버레이 (진행률 상태 격리) */}
          <PdfProgressOverlay
            ref={pdfProgressRef}
            onCancel={() => {
              pdfCancelRef.current = true
            }}
          />
        </div>
      </Dialog>
    </div>
  )
}

const createReport = async ({
  filter,
  template,
  setLoading,
  setReportSections,
  sessionsInfo,
  metricList,
  selectedUnitList,
  players,
  t,
  activity,
  session,
  interval,
  translateKey,
  setPdfSessionDetails,
}: {
  filter: ReportChartFilter
  template: ReportTemplate
  setLoading: Dispatch<SetStateAction<boolean>>
  setReportSections: Dispatch<SetStateAction<TypeSection[]>>
  sessionsInfo: Session[]
  interval: Interval
  translateKey: (key: string) => string | undefined
  setPdfSessionDetails: Dispatch<SetStateAction<SectionSessionDetails | undefined>>
} & ReportCommonProps &
  ReportActivitySessionProps) => {
  setLoading(true)
  if (!template || !template.content) {
    return
  }
  const metricMap: MetricMapType =
    metricList?.reduce((map, obj) => {
      map.set(obj.key, obj)

      return map
    }, new Map()) || new Map()

  removeSpecialCharacterInAllSource({ activity, session })

  const reportSectionsNew: Array<TypeSection> = []

  for (const [index, section] of template.content.entries()) {
    if (!section) {
      continue
    }
    switch (section.section) {
      case SECTION_TYPE.SUMMARY:
        {
          const settings = section.settings as SummarySettings[]
          if (settings) {
            const sectionSummary = createSummary({
              summarySettings: settings,
              metricMap,
              selectedUnitList,
              session,
              activity,
              activitySessionType: filter.activitySessionType,
            })
            if (sectionSummary) {
              reportSectionsNew[index] = sectionSummary
            }
          }
        }
        break
      case SECTION_TYPE.GRAPH:
        {
          const settings = section.settings as GraphSettings[]
          if (settings) {
            const sectionGraph = createGraph({
              graphSettings: settings,
              metricMap,
              metricList,
              selectedUnitList,
              players,
              t,
              translateKey,
            })

            if (sectionGraph) {
              reportSectionsNew[index] = sectionGraph
            }
          }
        }
        break
      case SECTION_TYPE.SESSION_DETAILS:
        {
          const settings = section.settings as SessionDetailsSettings[]
          if (settings) {
            const sectionSessionDetails = createSessionDetails({
              sessionDetailsSettings: settings,
              oldData: null,
              metricMap,
              filter,
              sessionsInfo,
              t,
              activity,
              session,
              metricList,
              selectedUnitList,
              interval: Number(interval.split("m")[0]),
              translateKey,
            })
            if (sectionSessionDetails) {
              reportSectionsNew[index] = sectionSessionDetails
            }

            const pdfSectionSessionDetails = createSessionDetails({
              sessionDetailsSettings: settings,
              oldData: null,
              metricMap,
              filter,
              sessionsInfo,
              t,
              activity,
              session,
              metricList,
              selectedUnitList,
              interval: Number(interval.split("m")[0]),
              translateKey,
              isPdf: true,
            })
            if (pdfSectionSessionDetails) {
              setPdfSessionDetails(pdfSectionSessionDetails)
            }
          }
        }
        break
      case SECTION_TYPE.SCATTER_GRAPH:
        {
          const settings = section.settings as ScatterChartSettings[]
          if (settings) {
            const sectionScatterChart = createScatter({
              session,
              activity,
              metricList,
              selectedUnitList,
              scatterChartSettings: settings,
              translateKey,
              activitySessionType: filter.activitySessionType,
            })

            if (sectionScatterChart) {
              reportSectionsNew[index] = sectionScatterChart
            }
          }
        }
        break
      case SECTION_TYPE.TACTIC_MAP:
        {
          const settings = section.settings as TacticMapSettings

          reportSectionsNew[index] = new SectionTacticMap({
            type: SECTION_TYPE.TACTIC_MAP,
            title: settings.title,
            settings: settings,
          })
        }
        break
      case SECTION_TYPE.PERFORMANCE_PROFILE:
        {
          const settings = section.settings as PerformanceProfileSettings

          reportSectionsNew[index] = new SectionPerformanceProfile({
            type: SECTION_TYPE.PERFORMANCE_PROFILE,
            title: settings.title,
            settings: settings,
          })
        }
        break
      case SECTION_TYPE.CUSTOM_TABLE:
        {
          const settings = section.settings as StatsSettings

          reportSectionsNew[index] = {
            type: SECTION_TYPE.CUSTOM_TABLE,
            settings: settings,
          }
        }
        break
    }
  }
  setReportSections(
    (reportSectionsNew as SectionGraph[]).map((section) => ({
      ...section,
      isLoading: false,
    })),
  )
  setLoading(false)
}

const createSummary = ({
  summarySettings,
  metricMap,
  selectedUnitList,
  session,
  activity,
  activitySessionType,
}: {
  summarySettings: SummarySettings[]
  metricMap: MetricMapType
  selectedUnitList: Unit[]
  activitySessionType: ACTIVITY_SESSION_TYPE
} & ReportActivitySessionProps): SectionSummary | null =>
  // 섹션 생성 규칙은 PDF 빌더와 공유하는 buildSummarySection 하나에만 둔다 (#75)
  buildSummarySection({
    summarySettings,
    metricMap,
    selectedUnitList,
    playerSummary: selectPlayerSummaryByMode(activitySessionType, activity, session),
  })

const createGraph = ({
  graphSettings,
  metricList,
  selectedUnitList,
  metricMap,
  players,
  t,
  translateKey,
}: {
  graphSettings: GraphSettings[]
  metricMap: MetricMapType
  translateKey: (key: string) => string | undefined
} & ReportCommonProps) =>
  // 섹션 생성 규칙은 PDF 빌더와 공유하는 buildGraphSection 하나에만 둔다 (#75)
  buildGraphSection({
    graphSettings,
    metricList,
    selectedUnitList,
    metricMap,
    players,
    t,
    translateKey,
  })

const createSessionDetails = ({
  sessionDetailsSettings,
  oldData,
  selectedUnitList,
  metricList,
  metricMap,
  filter,
  sessionsInfo,
  t,
  activity,
  session,
  interval,
  translateKey,
  isPdf = false,
}: {
  sessionDetailsSettings: SessionDetailsSettings[]
  oldData: SectionSessionDetails | null
  metricMap: MetricMapType
  filter: ReportChartFilter
  sessionsInfo: Session[]
  interval: number
  translateKey: (key: string) => string | undefined
  isPdf?: boolean
} & Omit<ReportCommonProps, "players"> &
  ReportActivitySessionProps) => {
  if (!sessionDetailsSettings || !metricList || !selectedUnitList) {
    return null
  }
  const settings = structuredClone(sessionDetailsSettings)

  const sessionDetailsData: Array<SessionDetailsData> = []
  settings.forEach((itemSettings, indexSettings) => {
    const oldDataSessionDetailsData =
      oldData && oldData.sessionDetailsData[indexSettings]
        ? oldData.sessionDetailsData[indexSettings]
        : null

    if (isPdf) {
      ;[1, 5, 15].map((intervalStatic) => {
        const sessionDetailsDataItem = createSessionDetailsSingleItem({
          sessionDetailsSettings: itemSettings,
          oldData: oldDataSessionDetailsData,
          metricMap,
          filter,
          sessionsInfo,
          metricList,
          selectedUnitList,
          t,
          activity,
          session,
          interval: intervalStatic,
          translateKey,
        })
        if (sessionDetailsDataItem) {
          sessionDetailsData.push(sessionDetailsDataItem)
        }
      })
    } else {
      const sessionDetailsDataItem = createSessionDetailsSingleItem({
        sessionDetailsSettings: itemSettings,
        oldData: oldDataSessionDetailsData,
        metricMap,
        filter,
        sessionsInfo,
        metricList,
        selectedUnitList,
        t,
        activity,
        session,
        interval,
        translateKey,
      })
      if (sessionDetailsDataItem) {
        sessionDetailsData.push(sessionDetailsDataItem)
      }
    }
  })

  return new SectionSessionDetails(sessionDetailsData)
}

const createSessionDetailsSingleItem = ({
  sessionDetailsSettings,
  oldData,
  metricMap,
  filter,
  sessionsInfo,
  metricList,
  selectedUnitList,
  t,
  activity,
  session,
  interval,
  translateKey,
}: {
  sessionDetailsSettings: SessionDetailsSettings
  oldData: SessionDetailsData | null
  metricMap: MetricMapType
  filter: ReportChartFilter
  sessionsInfo: Session[]
  interval: number
  translateKey: (key: string) => string | undefined
} & Omit<ReportCommonProps, "players"> &
  ReportActivitySessionProps) => {
  if (!sessionDetailsSettings || !metricList || !selectedUnitList) {
    return null
  }
  // 화면에서 바꾼 인터벌·섹션 내 세션 선택(oldData)은 유지한다
  const intervalMin = oldData ? oldData.interval : interval
  const selectedSessionIds =
    oldData && oldData.sessionSelected
      ? oldData.sessionSelected
      : structuredClone(filter.selectedSessionIds)

  // 데이터 구성 규칙은 PDF 빌더와 공유하는 buildSessionDetailsData 하나에만 둔다 (#75)
  return buildSessionDetailsData({
    settings: sessionDetailsSettings,
    metricMap,
    metricList,
    selectedUnitList,
    activitySessionType: filter.activitySessionType,
    selectedSessionIds,
    sessionSelectedRoot: filter.selectedSessionIds,
    sessionsInfo,
    sessionPlayerSummary: session?.playerSummary ?? null,
    sessionIntervalSummary: session?.intervalSummary[intervalMin] ?? null,
    activityIntervalSummary: activity?.intervalSummary[intervalMin] ?? null,
    interval: intervalMin,
    t,
    translateKey,
  })
}

const createScatter = ({
  session,
  activity,
  metricList,
  selectedUnitList,
  scatterChartSettings,
  translateKey,
  activitySessionType,
}: {
  scatterChartSettings: ScatterChartSettings[]
  metricList: MetricMeta[]
  selectedUnitList: Unit[]
  translateKey: (key: string) => string | undefined
  activitySessionType: ACTIVITY_SESSION_TYPE
} & ReportActivitySessionProps) => {
  if (isEmpty(scatterChartSettings) || !session || !activity) {
    return null
  }

  const players = cloneDeep(
    selectPlayerSummaryByMode(activitySessionType, activity, session)?.players,
  )
  const metricMap: MetricMapType =
    metricList?.reduce((map, obj) => {
      map.set(obj.key, obj)

      return map
    }, new Map()) || new Map()

  // 축·점 데이터 규칙은 PDF 빌더와 공유하는 buildScatterSection 하나에만 둔다 (#75)
  return buildScatterSection({
    scatterChartSettings,
    players,
    metricMap,
    selectedUnitList,
    translateKey,
  })
}
